'use client';
/** @jsxImportSource @emotion/react */

import { FC } from 'react';
import Button from '../Buttons';
import Icon from '../Icon';
import { IconNames } from '../Icon/iconMap';
import Typography from '../Typography';
import SkillsBlock from './SkillsBlock';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { stripHtml } from 'string-strip-html';
import Markdown from 'markdown-to-jsx';
import { usePathname } from 'next/navigation';

interface TextBlockProps {
	title: string;
	text: string | null;
}

const removeStyles = (text: string) => {
	const t = text
		?.replace?.(/("|;)\s*font-[^;"]+/gi, '')
		.replace?.(/("|;)\s*color:[^;"]+/gi, '')
		.replace?.(/(\n)+/gi, '\n\n');
	return t;
};

const isHTML = (text: string) => /<([a-z][a-z0-9]*)\b[^>]*>(.*?)<\/\1>/i.test(text);

const TextBlock: FC<TextBlockProps> = ({ title, text }) =>
	text ? (
		<div>
			<Typography variant="sub-headline-2" className="mt-16 text-2xl">
				{title}
			</Typography>
			{isHTML(text) ? (
				<div className="markdown-css" dangerouslySetInnerHTML={{ __html: removeStyles(text) }} />
			) : (
				<Markdown className="mb-24 text-lg leading-normal markdown-css">{removeStyles(text)}</Markdown>
			)}
		</div>
	) : null;

interface IconBlockProps {
	icon: IconNames;
	label: string;
	text: string | null;
}

const IconBlock: FC<IconBlockProps> = ({ icon, label, text }) => {
	if (!text) return null;

	const isHTMLContent = isHTML(text);

	// Se for HTML, renderiza sem o label (pois o HTML já contém o título)
	if (isHTMLContent) {
		return (
			<div className="mb-12">
				<div className="markdown-css" dangerouslySetInnerHTML={{ __html: removeStyles(text) }} />
			</div>
		);
	}

	// Se não for HTML, renderiza com label e ícone normalmente
	return (
		<div className="mb-12 flex justify-start items-start">
			<Icon name={icon} className="text-brand-main text-[2.4rem] mr-4 mt-2" />
			<Typography variant="body-2" className="font-bold mb-0 text-[2.2rem]">
				{label}
				<Typography variant="body-2" asEl="span" className="ml-4 text-[2.2rem]">
					<Markdown className="mb-24 text-lg leading-normal markdown-css">{removeStyles(text)}</Markdown>
				</Typography>
			</Typography>
		</div>
	);
};

interface JobDetailProps {
	id?: number | null;
	code?: string | null;
	title?: string | null;
	companyName?: string | null;
	address?: string | null;
	characteristics?: string | null;
	benefits?: string | null;
	workHours?: string | null;
	education?: string | null;
	salary?: string | null;
	description?: string | null;
	knowledges?: any;
	onApply: () => void;
	isApplied: boolean;
	isLoggedIn: boolean;
	className: string;
}

const JobDetail: FC<JobDetailProps> = ({
	id = null,
	code = null,
	title = null,
	companyName = null,
	address = null,
	characteristics = null,
	benefits = null,
	education = null,
	workHours = null,
	salary = null,
	description = null,
	knowledges = {},
	onApply,
	isApplied,
	isLoggedIn,
	className = '',
}) => {
	const pathname = usePathname();

	// Função para copiar o link da vaga
	const handleCopyLink = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		// Usa o domínio atual (funciona em localhost e produção)
		const origin = typeof window !== 'undefined' ? window.location.origin : '';
		const url = `${origin}${pathname}`;

		try {
			// API moderna
			// @ts-ignore - nem todos ambientes tipam clipboard
			if (navigator?.clipboard?.writeText) {
				// @ts-ignore
				navigator.clipboard.writeText(url);
			} else {
				// Fallback simples
				const textarea = document.createElement('textarea');
				textarea.value = url;
				textarea.style.position = 'fixed';
				textarea.style.left = '-9999px';
				document.body.appendChild(textarea);
				textarea.focus();
				textarea.select();
				document.execCommand('copy');
				textarea.remove();
			}

			// Feedback mínimo: altera apenas o texto (mantém o ícone) por 1.2s
			const btn = e.currentTarget;
			const label = btn.querySelector('[data-label]') as HTMLElement | null;
			const originalText = label?.textContent;
			if (label) label.textContent = 'Copiado!';
			setTimeout(() => {
				if (label) label.textContent = originalText || 'Compartilhar vaga';
			}, 1200);
		} catch {
			// Sem alert/tooltip para manter simples como pedido
		}
	};

	return (
		<div className={`pt-0 pb-16 py px-0 md:p-0 text-base-grey-5 ${className}`}>
			<div className="flex items-center justify-between mb-2">
				<Typography variant="sub-headline-3" className="text-brand-main">
					#{code}
				</Typography>
				<button
					onClick={handleCopyLink}
					className="text-xs sm:text-sm px-3 py-1 border border-brand-main text-brand-main hover:bg-brand-main hover:text-white transition-all rounded-full font-bold"
					type="button"
				>
					<span className="inline-flex items-center gap-2">
						{/* Ícone de compartilhar */}
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
							<path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
						</svg>
						<span data-label>Compartilhar vaga</span>
					</span>
				</button>
			</div>
			<Typography variant="headline-4" asEl="h2" className="text-[3.2rem] md:text-[4.2rem]">
				{title}
			</Typography>
			{companyName ? (
				<Typography variant="headline-4" asEl="h3" className="text-2xl text-base-grey-4">
					{companyName}
				</Typography>
			) : null}

			<div className="flex flex-col gap-32">
				<div>
					<TextBlock title={'Sobre a vaga'} text={description} />

					<TextBlock title={'Perfil Comportamental'} text={characteristics} />
				</div>

				<div className="mt-2 md:mt-4">
					<IconBlock icon="location" label={'Local:'} text={address} />
					<IconBlock icon="clock" label={'Horário de trabalho:'} text={workHours} />
					{/* <IconBlock icon="money" label={'Salário:'} text={salary} /> */}
					<IconBlock icon="heart" label={'Benefícios:'} text={benefits} />
					<IconBlock icon="schooling" label={'Formação:'} text={education} />
				</div>
				{knowledges && Object.values(knowledges).some(knowledge => knowledge != null) ? (
					<div>
						<Typography variant="body-1" className="font-bold mb-12">
							Conhecimentos Necessários
						</Typography>
						<SkillsBlock knowledges={knowledges} />
					</div>
				) : null}

				{isLoggedIn && !isApplied && (
					<div className="flex justify-start mb-44">
						<Button type="submit" variant="primary" size="gigantic" onClick={onApply}>
							Quero me candidatar
						</Button>
					</div>
				)}
				{isLoggedIn && isApplied && (
					<div className="flex justify-start mb-44">
						<Typography variant="body-1" className="text-base-grey-5 text-[2.2rem]">
							Você já está candidatado para esta vaga
						</Typography>
					</div>
				)}
				{!isLoggedIn && (
					<div className="flex justify-start mb-44">
						<Button type="link" variant="primary" size="gigantic" href={`/login?redirectUrl=${pathname}`}>
							Faça seu login para se candidatar
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};

export default JobDetail;
